import { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useClubSearch } from "../../../contexts/ClubSearchContext";
import { usePlayerSearch } from "../../../contexts/SearchPlayersContext";
import { groupColors, IProceedSimulationData, IProcessedArchetypeData, playermetricsGroup, SegmentData, teammetricsGrouped } from "../../../types/IRolesPlayedUtils";
import { ChemistryPlayerPositions } from "../../../types/IOption";
import { hasPlayersFromDifferentClubs } from "../../../utils/drawChemistrys";

export const formatLabel = (text: string, isTeam: boolean): string => {
  // if (text.toLowerCase().startsWith("xg")) {
  //   return "xG Shot".toUpperCase();
  // }
  // if(isTeam && text.toLocaleLowerCase() == "xgshotagainst"){
  //   return "xg shot against".toUpperCase();
  // }
  if (text.toLowerCase() === "interceptions") {
    return "Interceptions & Blocks".toUpperCase();
  }
  if (text.toLowerCase() === "passestofinalethird") {
    return "Passes To Final Third".toUpperCase();
  }
  if (isTeam && text.toLowerCase() === "dangerousopponenthalfrecoveries") {
    return "DANGEROUS BALL RECOVERIES".toUpperCase();
  }
  if (isTeam && text.toLowerCase() === "offensiveduels") {
    return "Off. Duels".toUpperCase();
  }
  // if (isTeam && text.toLowerCase() === "forwardpasses") {
  //   return "% FORWARD PASSES".toUpperCase();
  // }
  // if (isTeam && text.toLowerCase() === "ballrecoveries") {
  //   return "Pressing".toUpperCase();
  // }
  return text
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
    .toUpperCase();
};

export interface ISpiderSwapGraphProps {
  data?: IProceedSimulationData;
  width: number;
  height: number;
  colorIndex?: number;
  comparisonData?: IProcessedArchetypeData;
  isForComparison? : boolean;
  inPlayerIds?:number[];
  outPlayerIds?:number[];
  isComparisonBlackLine?: boolean;
  positionResults?: ChemistryPlayerPositions[]
  teamId?: number
}
 
export const SpiderSwapGraph: React.FC<ISpiderSwapGraphProps> = ({ data, width, height, colorIndex , comparisonData , isForComparison , inPlayerIds, outPlayerIds , isComparisonBlackLine, positionResults, teamId}) => {
  // debugger
  const svgRef = useRef(null);
  const{clubCompareId}= useClubSearch()
  const {comparePlayerId} = usePlayerSearch()

  useEffect(() => {
    if (!data || !data.player  || !data?.average  ) return;

    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    const radius = Math.min(innerWidth, innerHeight) / 2;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    const dataLength = data.player?.length;
    const angleScale = d3
      .scaleLinear()
      .domain([0, dataLength!])
      .range([0, 2 * Math.PI]);
     
    const radiusScale = d3
      .scaleLinear()
      .domain([0, 100])
      .range([0, radius]);
      
    const segments = data.player.map((d, i) => ({
      startAngle: angleScale(i),
      endAngle: angleScale(i + 1),
      value: d.value,
      afterValue: d.afterValue,
      segment: d.segment,
      rawScore: d.rawScore,
    }));

    const compareSegments = data.player.map((d, i) => {
      const matching = comparisonData?.player.find(avg => avg.segment === d.segment);    
      return {
        startAngle: angleScale(i),
        endAngle: angleScale(i + 1),
        value: matching?.value ?? 0,         // fallback if segment is missing
        segment: d.segment,
        rawScore: matching?.rawScore ?? 0,
      };
    });

    const compareSegmentsBorderData = data?.average;
    const metricsGrouped = data.isTeam ? teammetricsGrouped : playermetricsGroup

    // Draw background segments
    svg
      .selectAll(".segment")
      .data(segments)
      .join("path")
      .attr("class", "segment")
      .attr( "d",d3
          .arc<SegmentData>()
          .innerRadius(8)
          .outerRadius((d) => radiusScale(d.value))
          .startAngle((d) => d.startAngle)
          .endAngle((d) => d.endAngle)
      )
      .style("fill", (d: any) => {
          if (inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0 && hasPlayersFromDifferentClubs({positionResults: positionResults!, teamId: teamId!})) {
            return "#CFE6F4";
          }
          if (metricsGrouped.finalThird.includes(d.segment)) {
            return groupColors.finalThird;
          } else if (metricsGrouped.buildUpPlay.includes(d.segment)) {
            return groupColors.buildUpPlay;
          } else if (metricsGrouped.defensivePlay.includes(d.segment)) {
            return groupColors.defensivePlay;
          }
          return "#D3D3D3";
      })
      .style("stroke", "white")
      .style("stroke-width", 1)
      .attr("pointer-events", "none");

    //draw segments for after swap value
    // CFE6F4
   // draw difference arcs (delta only)
    if (inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0) {
    svg
        .selectAll(".segment-diff")
        .data(segments)
        .join("path")
        .attr("class", "segment-diff")
        .attr(
        "d",
        d3.arc<SegmentData>()
            .innerRadius((d) => {
            const v = radiusScale(d.value);
            const a = radiusScale(d.afterValue!);
            return a > v ? v : a; // smaller one
            })
            .outerRadius((d) => {
            const v = radiusScale(d.value);
            const a = radiusScale(d.afterValue!);
            return a > v ? a : v; // bigger one
            })
            .startAngle((d) => d.startAngle)
            .endAngle((d) => d.endAngle)
        )
        .style("fill", (d: any) => {
            if (metricsGrouped.finalThird.includes(d.segment)) {
                return d.afterValue! > d.value ?  "#00D1A8" : '#FF3864';
            } else if (metricsGrouped.buildUpPlay.includes(d.segment)) {
                return d.afterValue! > d.value ?  "#00D1A8" : '#FF3864';
            } else if (metricsGrouped.defensivePlay.includes(d.segment)) {
                return d.afterValue! > d.value ?  "#00D1A8" : '#FF3864';
            }
            return "#D3D3D3"; 
        })
        
        // .style("fill", (d) =>
        //   d.afterValue! > d.value ? "#CFE6F4" : "#e7b0acff" // green = increase, red = decrease
        // )
        // .style("opacity", 0.6)
        .style("stroke", (d: any) => { return d.afterValue! > d.value ? "none" : "#DADADA"})
        .style("stroke-width", 1)
        .attr("pointer-events", "none");

        // Outer border only for increases
    // svg
    //     .selectAll(".segment-diff-border")
    //     .data(segments.filter((d) => d.afterValue! > d.value)) // only increases
    //     .join("path")
    //     .attr("class", "segment-diff-border")
    //     .attr(
    //     "d",
    //     d3.arc<SegmentData>()
    //         .innerRadius((d) => radiusScale(d.afterValue!)) // outer edge only
    //         .outerRadius((d) => radiusScale(d.afterValue!))
    //         .startAngle((d) => d.startAngle)
    //         .endAngle((d) => d.endAngle)
    //     )
    //     .style("fill", "none")
    //     .style("stroke", "#00D1A8")
    //     .style("stroke-width", 2);

    //     svg
    //     .selectAll(".segment-diff-border-dash-gray")
    //     .data(segments.filter((d) => d.afterValue! > d.value)) // only increases
    //     .join("path")
    //     .attr("class", "segment-diff-border-dash-gray")
    //     .attr(
    //     "d",
    //     d3.arc<SegmentData>()
    //         .innerRadius((d) => radiusScale(d.value!)) // outer edge only
    //         .outerRadius((d) => radiusScale(d.value!))
    //         .startAngle((d) => d.startAngle)
    //         .endAngle((d) => d.endAngle)
    //     )
    //     .style("fill", "none")
    //     .style("stroke", "gray")
    //     .style("stroke-width", 1)
    //     .style("stroke-dasharray", "3 5"); // dotted/dashed effect

    //     svg
    //     .selectAll(".segment-diff-border-gray")
    //     .data(segments.filter((d) => d.afterValue! < d.value)) // only decreases
    //     .join("path")
    //     .attr("class", "segment-diff-border-gray")
    //     .attr(
    //         "d",
    //         d3.arc<SegmentData>()
    //         .innerRadius((d) => radiusScale(d.value!)) // border at afterValue (inner edge)
    //         .outerRadius((d) => radiusScale(d.value!))
    //         .startAngle((d) => d.startAngle)
    //         .endAngle((d) => d.endAngle)
    //     )
    //     .style("fill", "none")
    //     .style("stroke", "gray")
    //     .style("stroke-width", 1)
    //     .style("stroke-dasharray", "3 5"); // dotted/dashed effect
    //     svg
    //     .selectAll(".segment-diff-border-red")
    //     .data(segments.filter((d) => d.afterValue! < d.value)) // only increases
    //     .join("path")
    //     .attr("class", "segment-diff-border-red")
    //     .attr(
    //     "d",
    //     d3.arc<SegmentData>()
    //         .innerRadius((d) => radiusScale(d.afterValue!)) // outer edge only
    //         .outerRadius((d) => radiusScale(d.afterValue!))
    //         .startAngle((d) => d.startAngle)
    //         .endAngle((d) => d.endAngle)
    //     )
    //     .style("fill", "none")
    //     .style("stroke", "#FF3864")
    //     .style("stroke-width", 2);

// Define markers once
  const defs = svg.select("defs");
  if (defs.empty()) {
    svg.append("defs").selectAll("marker")
      .data(["arrow-up", "arrow-down"])
      .join("marker")
      .attr("id", d => d)
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 5)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", d => d === "arrow-up" ? "#00D1A8" : "#FF3864");
  }

  // Filter: only keep segments where value != afterValue
  const arrowSegments = segments.filter(d => d.afterValue !== d.value);

  // Draw arrows
  // svg.selectAll(".segment-arrow")
  //   .data(arrowSegments)
  //   .join("line")
  //   .attr("class", "segment-arrow")
  //   .attr("x1", d => {
  //     const minR = Math.min(radiusScale(d.value), radiusScale(d.afterValue!));
  //     const maxR = Math.max(radiusScale(d.value), radiusScale(d.afterValue!));
  //     const angle = (d.startAngle + d.endAngle) / 2;
  //     return (d.afterValue! > d.value ? minR : maxR) * Math.cos(angle - Math.PI/2);
  //   })
  //   .attr("y1", d => {
  //     const minR = Math.min(radiusScale(d.value), radiusScale(d.afterValue!));
  //     const maxR = Math.max(radiusScale(d.value), radiusScale(d.afterValue!));
  //     const angle = (d.startAngle + d.endAngle) / 2;
  //     return (d.afterValue! > d.value ? minR : maxR) * Math.sin(angle - Math.PI/2);
  //   })
  //   .attr("x2", d => {
  //     const minR = Math.min(radiusScale(d.value), radiusScale(d.afterValue!));
  //     const maxR = Math.max(radiusScale(d.value), radiusScale(d.afterValue!));
  //     const angle = (d.startAngle + d.endAngle) / 2;
  //     return (d.afterValue! > d.value ? maxR : minR) * Math.cos(angle - Math.PI/2);
  //   })
  //   .attr("y2", d => {
  //     const minR = Math.min(radiusScale(d.value), radiusScale(d.afterValue!));
  //     const maxR = Math.max(radiusScale(d.value), radiusScale(d.afterValue!));
  //     const angle = (d.startAngle + d.endAngle) / 2;
  //     return (d.afterValue! > d.value ? maxR : minR) * Math.sin(angle - Math.PI/2);
  //   })
  //   .attr("stroke", d => d.afterValue! > d.value ? "#00D1A8" : "#FF3864")
  //   .attr("stroke-width", 1.2)
  //   .attr("marker-end", d => d.afterValue! > d.value ? "url(#arrow-up)" : "url(#arrow-down)")
  //   .style("pointer-events", "none");

    }

      svg
        .selectAll(".segment-lines")
        .data(segments)
        .join("path")
        .attr("class", "segment-lines")
        .attr("d", (d) => {
          const startPoint = d3
            .arc<SegmentData>()
            .innerRadius(radiusScale(d.value))
            .outerRadius(radiusScale(d.value))
            .startAngle(d.startAngle)
            .endAngle(d.startAngle)(d);
          const endPoint = d3
            .arc<SegmentData>()
            .innerRadius(radius)
            .outerRadius(radius)
            .startAngle(d.startAngle)
            .endAngle(d.startAngle)(d);
          return `M ${startPoint!.split("M")[1]} L ${endPoint!.split("M")[1]}`;
        })
        .style("stroke", "#DADADA")
        .style("stroke-width", 1);
      
      //black border
      if(isComparisonBlackLine){
      svg
        .selectAll(".average-border")
        .data(compareSegmentsBorderData!)
        .join("path")
        .attr("class", "average-border")
        .attr("d", (d, i) => {
          const startAngle = angleScale(i);
          const endAngle = angleScale(i + 1);
          const currentRadius = radiusScale(d.value);
          const nextRadius = radiusScale(compareSegmentsBorderData![(i + 1) % compareSegmentsBorderData?.length!].value);
          // Calculate current arc points
          const x1 = Math.sin(startAngle) * currentRadius;
          const y1 = -Math.cos(startAngle) * currentRadius;
          const x2 = Math.sin(endAngle) * currentRadius;
          const y2 = -Math.cos(endAngle) * currentRadius;
          // Calculate next arc's starting point
          const x3 = Math.sin(endAngle) * nextRadius;
          const y3 = -Math.cos(endAngle) * nextRadius;
          // Create path: Move to start -> Draw arc -> Draw line to next radius
          return `M ${x1},${y1} 
                  A ${currentRadius},${currentRadius} 0 0,1 ${x2},${y2}
                  L ${x3},${y3}`;
        })
        .style("fill", "none")
        .style("stroke", "#000000")
        .style("stroke-width", 2);
      }


      svg.append("circle").attr("r", radius).style("fill", "none").style("stroke", "#C2C2C2").style("stroke-width", 1);

      // Add labels
      svg
        .selectAll(".label-container")
        .data(segments)
        .join("g")
        .attr("class", "label-container")
        .attr("transform", (d) => {
          const angle = (d.startAngle + d.endAngle) / 2;
          const x = Math.sin(angle) * (radius + 6);
          const y = -Math.cos(angle) * (radius + 6);
          return `translate(${x},${y})`;
        })
        .each(function (d) {
          const container = d3.select(this);
          const words = formatLabel(d.segment, (data.isTeam??false)).toUpperCase().split(" ");
          const lineHeight = 8; // Height for each line of text
          // Create first line with first two words
          const firstLine = container
            .append("text")
            .attr("class", "label")
            .attr("text-anchor", "middle")
            .attr("dy", "0.35em")
            .text(words.slice(0, 2).join(" "))
            .style("font-family", "DM Mono")
            .style("font-size", "10px")
            .style("font-weight", "500")
            .style("fill", "#7C7D79");
          let bbox = firstLine.node()!.getBBox();
          let totalHeight = bbox.height;
          let maxWidth = bbox.width;
          // If there are more words, create second line
          if (words.length > 2) {
            const secondLine = container
              .append("text")
              .attr("class", "label")
              .attr("text-anchor", "middle")
              .attr("dy", `${lineHeight}px`)
              .text(words.slice(2).join(" "))
              .style("font-family", "DM Mono")
              .style("font-size", "10px")
              .style("font-weight", "500")
              .style("fill", "#7C7D79");
            const secondBbox = secondLine.node()!.getBBox();
            totalHeight = bbox.height + secondBbox.height;
            maxWidth = Math.max(bbox.width, secondBbox.width);
            // Adjust the position of both lines to be centered
            const totalBboxHeight = totalHeight;
            const firstLineY = -totalBboxHeight / 2 + bbox.height / 2;
            const secondLineY = firstLineY + lineHeight;
            firstLine.attr("y", firstLineY);
            secondLine.attr("y", secondLineY);
          }
          // Create background rectangle for label
          container
            .insert("rect", "text")
            .attr("x", -maxWidth / 2 - 8)
            .attr("y", -totalHeight / 2 - 4)
            .attr("width", maxWidth + 16)
            .attr("height", totalHeight + 8)
            .attr("rx", 2)
            .attr("fill", "white");
        });

        //on hover only
        svg.selectAll(".hover-area")
          .data(segments)
          .join("path")
          .attr("class", "hover-area")
          .attr("d",d3.arc<SegmentData>()
                      .innerRadius(8)
                      .outerRadius(radius)
                      .startAngle((d) => d.startAngle)
                      .endAngle((d) => d.endAngle)
          )
          .style("fill", "transparent")
          .style("cursor", "pointer")
          .on("mouseover", function (event, d) {
            if (!(inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0)) {
              svg
                .selectAll(".segment")
                .filter((seg: any) => seg.segment === d.segment)
                .style("filter", "drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.4))")
                .style("fill", (d: any) => {
                if (metricsGrouped.finalThird.includes(d.segment)) {
                  return groupColors.finalThird;
                } else if (metricsGrouped.buildUpPlay.includes(d.segment)) {
                  return groupColors.buildUpPlay;
                } else if (metricsGrouped.defensivePlay.includes(d.segment)) {
                  return groupColors.defensivePlay;
                }
                return "#D3D3D3";
              })
              .style("stroke", "#FFFFFF")
              .style("stroke-width", 2)
              .style("opacity", 1);
            }

            if (inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0) {
              svg
                .selectAll(".segment-diff")
                .filter((seg: any) => seg.segment === d.segment)
                .style("filter", "drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.4))")
                .style("stroke", "#FFFFFF")
                .style("stroke-width", 2)
                .style("opacity", 1);
            }
        
            const tooltip = svg.append("g").attr("class", "tooltip-spider").style("pointer-events", "none");
            const angle = (d.startAngle + d.endAngle) / 2;
            const tooltipRadius = radiusScale(d.value) / 2;
            const x = Math.sin(angle) * tooltipRadius;
            const y = -Math.cos(angle) * tooltipRadius;
            let tooltipText = d.segment === "pressing"? `${d?.rawScore!.toFixed(2)} PPDA`
              : (d.segment === "passLength" || d.segment === "dribbleDistanceFromOpponentGoal"
                ? d?.rawScore!.toFixed(2)
                : `${d?.rawScore!.toFixed(2)} per '90`);
            const text = tooltip
              .append("text")
              .attr("text-anchor", "middle")
              .attr("dy", "0.35em")
              .text(tooltipText)
              .style("font-family", "Aeonik")
              .style("font-size", "16px")
              .style("font-weight", "500")
              .style("fill", "#000000");
            const bbox = text.node()!.getBBox();
            tooltip
              .insert("rect", "text")
              .attr("x", bbox.x - 8)
              .attr("y", bbox.y - 4)
              .attr("width", bbox.width + 16)
              .attr("height", bbox.height + 8)
              .attr("rx", 2)
              .attr("fill", "white")
              .style("filter", "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))");
            tooltip.attr("transform", `translate(${x},${y})`);
          })
          .on("mouseout", function (event, d) {
            if (inPlayerIds?.length! > 0 && outPlayerIds?.length! > 0) {
              svg
                .selectAll(".segment-diff")
                .filter((seg: any) => seg.segment === d.segment)
                .style("filter", "none")
                .style("stroke", (d: any) => { return d.afterValue! > d.value ? "none" : "#DADADA"})
                .style("stroke-width", 1);
            } else {
              svg
                .selectAll(".segment")
                .filter((seg: any) => seg.segment === d.segment)
                .style("filter", "none")
                .style("fill", (d: any) => {
                if (metricsGrouped.finalThird.includes(d.segment)) {
                  return groupColors.finalThird;
                } else if (metricsGrouped.buildUpPlay.includes(d.segment)) {
                  return groupColors.buildUpPlay;
                } else if (metricsGrouped.defensivePlay.includes(d.segment)) {
                  return groupColors.defensivePlay;
                }
                  return "#D3D3D3";
                })
                .style("stroke", "white")
                .style("stroke-width", 1)
                .style("opacity", 1);
            }

            svg.selectAll(".tooltip-spider").remove();
          });
    }, [data, width, height , data?.average , comparisonData?.average, positionResults,teamId]);
    
    return <svg ref={svgRef}></svg>;
};
